import { useEffect, useCallback } from "react";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import useLogger from "./useLogger";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { debugLog } from "@/utils/debugLog";
import { usePlayerTick } from "@/features/tutor/hooks/usePlayerTick";
import { resumePlaybackHoldRef } from "@/features/tutor/vo-demo/runtime/resumePlaybackHold";

export const useTutorActionQueue = () => {
  const { subscribe, emit, pcmPlayerRef } = useRuntimeContext();
  const { addLog } = useLogger();

  // The caption queue has passed a pause ref since it was written; this one
  // never did, so every reveal rode whichever clock happened to be running.
  // A held resume turn has no audio clock to ride at all when muted.
  const { enqueue, cancelPending } = usePlayerTick(pcmPlayerRef, {
    isPausedRef: resumePlaybackHoldRef,
  });

  const clearQueue = useCallback(() => {
    cancelPending();
  }, [cancelPending]);

  useEffect(() => {
    const unsubTutorAction = subscribe(
      "TUTOR_ACTION",
      ({ chunk_data, turn_id }) => {
        try {
          if (turn_id !== useTurnStore.getState().getTurnId()) {
            addLog(
              `Ignoring Tutor Action with turn_id ${turn_id} - not the current turn ${useTurnStore.getState().getTurnId()}`,
            );
            return;
          }
          const parsedData = JSON.parse(chunk_data);
          const currentTimeSec = (pcmPlayerRef?.current?.playedMs ?? 0) / 1000;

          const action = {
            // `??`, not `||`: the backend stamps action_time 0.0 to mean "apply
            // now" (manual_trigger_action -> build_manual_action_chunk). `||`
            // treated that as missing and pushed it a second into the future,
            // which sorts it behind the LLM's own actions in the playedMs-ordered
            // queue and applies them out of order.
            action_time: parsedData.action_time ?? currentTimeSec + 1,
            action_duration: parsedData.action_duration || 0,
            action_behavior:
              "action_behavior" in parsedData
                ? parsedData.action_behavior
                : true,
            tutor_action: JSON.parse(parsedData.tutor_action),
            processed: false,
            turn_id: turn_id,
          };
          debugLog("action pushed to tutor action queue", action);
          enqueue(action.action_time * 1000, () => {
            emit("TUTOR_ACTION_READY", { action });
            addLog(`[TUTOR_ACTION_READY] ${action.tutor_action.action}`);
          });
        } catch (error) {
          console.error("Error processing TUTOR_ACTION:", error);
        }
      },
    );

    return () => {
      cancelPending();
      unsubTutorAction();
    };
  }, [subscribe, enqueue, cancelPending, addLog, emit, pcmPlayerRef]);

  return { clearQueue };
};
